import { useState, useEffect } from "react";
import { ExpandedContent, Separator, TableContainer } from "./styles";
import { IItemHistorico } from "../../../../types";
import api from "../../../../../../../services/api";
import { CampoAlterado, DadosRecusaProfissional } from "./types";
import { mapearCamposAlterados, mapearDadosRecusaProfissional } from "./utils";
import BlocoRecusaProfissional from "./Blocos/BlocoRecusaProfissional";
import LogsVazio from "../../Compartilhado/LogsVazio";

interface ExpandedContentProps {
    item: IItemHistorico;
    onNavigateToFile?: (fileId: number) => void;
}

export default function ExpandedContentRecusaProfissional({ item }: ExpandedContentProps) {
    const [camposPrincipal, setCamposPrincipal] = useState<CampoAlterado[]>([]);
    const [dadosRecusa, setDadosRecusa] = useState<DadosRecusaProfissional | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!item.id) {
            setLoading(false);
            return;
        }

        const buscarDados = async () => {
            try {
                setLoading(true);
                const response = await api.get(`log-historico/${item.id}`);

                if (response.data.status === 'success') {
                    const logs = response.data.result || [];
                    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');
                    const logRecusa = logs.find((log: any) => log.entidade_nome === 'recusa_profissional');

                    if (logProposta) {
                        try {
                            const dadosExtra = logProposta.dados_extra;
                            const campos = await mapearCamposAlterados(logProposta, dadosExtra);
                            setCamposPrincipal(campos || []);
                        } catch (error) {
                            setCamposPrincipal([]);
                        }
                    } else {
                        setCamposPrincipal([]);
                    }

                    if (logRecusa) {
                        try {
                            const dadosExtra = logRecusa.dados_extra;
                            const recusa = mapearDadosRecusaProfissional(logRecusa, dadosExtra);
                            
                            if (recusa && recusa.id_usuario) {
                                const id = Number(recusa.id_usuario);
                                if (!isNaN(id) && id > 0 && !recusa.nome_profissional) {
                                    try {
                                        const userResponse = await api.get(`usuario/${id}`);
                                        if (userResponse?.data?.status === 'success' && userResponse?.data?.result?.nome) {
                                            recusa.nome_profissional = userResponse.data.result.nome;
                                        }
                                    } catch (error: any) {
                                        if (error?.response?.status && error.response.status !== 404) {
                                            recusa.nome_profissional = undefined;
                                        }
                                    }
                                }
                            }
                            
                            setDadosRecusa(recusa);
                        } catch (error) {
                            setDadosRecusa(null);
                        }
                    } else {
                        setDadosRecusa(null);
                    }
                } else {
                    setCamposPrincipal([]);
                    setDadosRecusa(null);
                }
            } catch (error: any) {
                console.error('Erro ao buscar dados do histórico:', error);
                setCamposPrincipal([]);
                setDadosRecusa(null);
            } finally {
                setLoading(false);
            }
        };

        buscarDados();
    }, [item.id]);

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    const temDadosValidos = camposPrincipal.length > 0 || dadosRecusa !== null;

    if (!temDadosValidos) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            <Separator />
            {camposPrincipal.length > 0 && (
                <div className="expanded-section">
                    <h4 className="section-title">Principal</h4>
                    <TableContainer>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="field-col">Campo</th>
                                    <th className="before-col">Antes</th>
                                    <th className="after-col">Depois</th>
                                </tr>
                            </thead>
                            <tbody>
                                {camposPrincipal.map((campo, index) => (
                                    <tr key={campo.campo} className={index > 0 ? 'border-top' : ''}>
                                        <td className="field-cell">{campo.campo}</td>
                                        <td className="before-cell">{campo.antes || '—'}</td>
                                        <td className="after-cell">
                                            <div className="after-content">
                                                <span className="highlight-bar" />
                                                <span>{campo.depois || '—'}</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </TableContainer>
                </div>
            )}
            {dadosRecusa && <BlocoRecusaProfissional dados={dadosRecusa} />}
        </ExpandedContent>
    );
}

